import React, { useState, FC, useEffect, useRef } from "react";
import { Box, DialogActions, DialogContent, Typography } from "@mui/material";
import { Form, Formik } from "formik";
import { InputPanel } from "./InputPanel";
import { PdpUrlFields } from "./PdpUrlFields";
import { BriefBrandAssetsSection } from "./BriefBrandAssetsSection";
import { PanelWrapper } from "./PanelWrapper";
import {
    BriefAttachments,
    BriefAttachmentsHandle,
} from "./BriefAttachments";
import { resolveProjectBrandId } from "../../utils";
import { projectBriefGenderList } from "../../pages/pro/createEditProfile/formFragments/constants";
import { FieldSelect } from "./FieldSelect";
import { useProfessions } from "../../hooks/useProfessions";
import { AgeRangeSelector } from "./AgeRangeSelector";
import { BriefProfessions } from "./BriefProfessions";
import { Button } from "../Button/Button";
import {
    IExpertQuestions,
    IVideoContent,
    initVideoContent,
    initExpertQuestions,
    expertQuestionSchema,
    videoContentSchema,
    briefPdpUrlRows,
    IBriefProfession,
    briefExpertTypeOptions,
    BriefAttachment,
    BRIEF_ALL_ASSET_KEYS
} from "./constants";
import * as yup from "yup";
import { BrandProject } from "../../pages/videoProduction/components/constants";

// Attachments are uploaded by the host AFTER it has persisted the brief, so a
// brief created in the same click has an id to attach to. The host must call
// uploadAttachments(briefId) before it refreshes or closes.
export interface BriefFormHelpers {
    uploadAttachments: (briefId: number) => Promise<void>;
}

interface Props {
    project?: BrandProject;
    loading?: boolean;
    publicToken?: string;
    publicBase?: string;
    onCancel: () => void;
    onSubmit: (data: IExpertQuestions, helpers: BriefFormHelpers) => void;
    onChange?: (data: IVideoContent) => void;
    onSave?: (data: any, helpers: BriefFormHelpers) => void;
}

const combinedSchema = videoContentSchema.concat(expertQuestionSchema);

export const BriefForm: FC<Props> = ({ project, loading, onCancel, onSubmit, onChange, onSave, publicToken, publicBase }) => {
    const { areProfessionsLoaded, getProfessions } = useProfessions();
    const professionsFormRef = useRef<{ mergePendingItems: () => any[]; hasPendingItems: () => boolean }>(null);

    // The professions tree is fetched once at app boot; if that request failed
    // (API restarting, transient error) the Profession/Expertise field would
    // otherwise never appear until a hard reload. Re-request here — the store
    // de-dupes and no-ops once loaded.
    useEffect(() => {
        if (!areProfessionsLoaded) getProfessions();
    }, [areProfessionsLoaded, getProfessions]);

    const convertLegacyProfessions = (brief: any): IBriefProfession[] => {
        if (brief?.profession_id) {
            return [{ profession_id: brief.profession_id, sub_profession_id: brief.sub_profession_id, order: 0 }];
        }
        return [];
    };

    const cleanProfessionsData = (professions: IBriefProfession[]): IBriefProfession[] => {
        if (!Array.isArray(professions)) return [];
        return professions
            .filter((prof) => prof?.profession_id > 0)
            .map((prof, index) => ({
                profession_id: prof.profession_id,
                sub_profession_id: prof.sub_profession_id || undefined,
                order: prof.order ?? index
            }));
    };

    const brief = project?.briefs?.[0];
    const [attachments, setAttachments] = useState<BriefAttachment[]>(
        brief?.attachments || [],
    );
    const savedAttachments = brief?.attachments;
    useEffect(() => {
        if (savedAttachments) setAttachments(savedAttachments);
    }, [savedAttachments]);
    const attachmentsRef = useRef<BriefAttachmentsHandle>(null);
    const [attachmentsQueued, setAttachmentsQueued] = useState(false);
    const helpers: BriefFormHelpers = {
        uploadAttachments: (briefId) =>
            attachmentsRef.current?.upload(briefId) || Promise.resolve(),
    };

    return (
        <Formik
            initialValues={{
                ...initVideoContent,
                ...initExpertQuestions,
                ...(brief
                    ? {
                          url: brief.url || "",
                          desires_pdp_url_id: brief.desires_pdp_url_id ?? null,
                          pdp_urls: briefPdpUrlRows(brief).map((link) => ({
                              desireid: link.desireid,
                              url: link.url || "",
                              desires_pdp_url_id:
                                  link.desires_pdp_url_id ?? null,
                          })),
                          name: brief.name || "",
                          short_name: brief.short_name || "",
                          description: brief.description || "",
                          location_request: brief.location_request || "",
                          brand_requests: (brief as any).brand_requests || "",
                          expert_gender: brief.expert_gender || "",
                          expert_type: (brief as any).expert_type || "either",
                          ageRange: [brief.expert_age_min, brief.expert_age_max],
                          professions: cleanProfessionsData(brief.professions || convertLegacyProfessions(brief)),
                          expert_interests: brief.expert_interests || "",
                          other: brief.other || "",
                          special_requests: brief.special_requests || "",
                          // Never-set (null) snapshot fields are seeded from the
                          // brand by BriefBrandAssetsSection once it loads.
                          ...Object.fromEntries(
                              BRIEF_ALL_ASSET_KEYS.map((key) => [key, (brief as any)[key] || ""])
                          ),
                          product_disclaimers: brief.product_disclaimers || {}
                      }
                    : {})
            }}
            validationSchema={combinedSchema as yup.ObjectSchema<any>}
            validateOnMount
            validateOnChange
            validateOnBlur
            enableReinitialize={false}
            onSubmit={(values) => {
                const mergedProfessions = professionsFormRef.current?.mergePendingItems() || values.professions;

                const expertPayload: IExpertQuestions = {
                    expert_gender: values.expert_gender,
                    expert_type: (values as any).expert_type || "either",
                    ageRange: values.ageRange,
                    professions: mergedProfessions,
                    expert_interests: values.expert_interests,
                    other: values.other,
                    special_requests: values.special_requests
                };
                onSubmit(expertPayload, helpers);
            }}
        >
            {({ values, submitForm, setFieldValue, setFieldTouched, dirty, isValid, errors, touched }) => {
                useEffect(() => {
                    if (onChange) {
                        const intake: IVideoContent = {
                            url: values.url,
                            desires_pdp_url_id: values.desires_pdp_url_id ?? null,
                            pdp_urls: values.pdp_urls,
                            name: values.name,
                            short_name: values.short_name,
                            description: values.description,
                            location_request: values.location_request,
                            brand_requests: values.brand_requests,
                            ...Object.fromEntries(
                                BRIEF_ALL_ASSET_KEYS.map((key) => [key, (values as any)[key]])
                            ),
                            product_disclaimers: values.product_disclaimers
                        };
                        onChange(intake);
                    }
                }, [
                    values.url,
                    values.desires_pdp_url_id,
                    values.pdp_urls,
                    values.name,
                    values.short_name,
                    values.description,
                    values.location_request,
                    values.brand_requests,
                    ...BRIEF_ALL_ASSET_KEYS.map((key) => (values as any)[key]),
                    values.product_disclaimers
                ]);

                const hasProfessions = Array.isArray(values.professions) && values.professions.length > 0;
                const professionsError = errors.professions;
                const hasOnlyProfessionsError = professionsError && Object.keys(errors).length === 1;
                const effectiveIsValid = hasOnlyProfessionsError && hasProfessions ? true : isValid;

                const handleSave = () => {
                    const mergedProfessions = professionsFormRef.current?.mergePendingItems() || values.professions;
                    const updatedValues = { ...values, professions: mergedProfessions };
                    if (onSave) {
                        onSave(updatedValues, helpers);
                    }
                };

                const handleSubmit = () => {
                    const mergedProfessions = professionsFormRef.current?.mergePendingItems() || values.professions;
                    setFieldValue("professions", mergedProfessions);
                    setTimeout(() => {
                        submitForm();
                    }, 0);
                };

                const isEditing = !!brief;

                const changed = dirty || attachmentsQueued;
                const canSubmit = isEditing
                    ? effectiveIsValid && changed
                    : effectiveIsValid;

                return (
                    <>
                        <DialogContent className="border-t border-border-token !p-6">
                            <Form>
                                <Box className="flex flex-col w-full gap-6">
                                    <div className="font-[900] text-[18px] leading-[132%] tracking-normal">
                                        Intake Form
                                    </div>
                                    <PdpUrlFields
                                        projectId={project?.id}
                                        publicToken={publicToken}
                                        publicBase={publicBase}
                                        body="If the product is live, please link its PDP"
                                        footer="Pick a PDP already saved for the product, or paste a new one to add it."
                                    />
                                    <InputPanel
                                        name="name"
                                        body="List the product name as you would like it to be first introduced in the video, and include its phonetic pronunciation*"
                                    />
                                    <InputPanel
                                        name="short_name"
                                        body="List 2-3 shortened product names that are approved to be used throughout the rest of the video*"
                                        footer="Note: This is for the purpose of a more natural narration from our expert. Please ensure the additional shorthand names are commonly used in other product presentations including store, CRM and online channels."
                                    />
                                    <InputPanel
                                        name="description"
                                        body="List the 3-5 most important product features and benefits you'd like to be highlighted in the video in order of priority/importance. Simple language is encouraged*"
                                        rows={3}
                                        footer="Note: To create an authentic review, the video will not be scripted, and your Expert will speak about these features and benefits in their own words. The video may also include other key points the Expert felt important to highlight."
                                    />
                                    <InputPanel
                                        name="location_request"
                                        body="List any requests for the filming location/setting"
                                        rows={3}
                                        footer="For Example: Film in a kitchen/dining room, Film outdoors and indoors, neutral background colors."
                                    />
                                    <InputPanel
                                        name="brand_requests"
                                        body="Any other specific brand requests:"
                                        rows={3}
                                    />
                                    <BriefBrandAssetsSection
                                        projectId={project?.id}
                                        publicToken={publicToken}
                                        publicBase={publicBase}
                                        brief={brief}
                                        disabled={loading}
                                    />

                                    <Box className="flex flex-col w-full gap-2">
                                        <div className="font-[900] text-[18px] leading-[132%] tracking-normal ">
                                            Expert Questions
                                        </div>
                                        <Typography
                                            variant="body2"
                                            fontFamily="CircularStd"
                                            className="text-text-2"
                                        >
                                            Please provide details on the type of Expert you'd like to engage for this
                                            video.
                                        </Typography>
                                    </Box>
                                    <PanelWrapper body="Gender*">
                                        <FieldSelect
                                            placeholder="Select Gender"
                                            options={projectBriefGenderList}
                                            name="expert_gender"
                                        />
                                    </PanelWrapper>
                                    <PanelWrapper
                                        body="Expert Type"
                                        footer="Industry Expert — a credentialed professional in the field (contractor, engineer, clinician, chef); best when a professional audience needs peer-level authority to trust the recommendation. Use-Case Expert — a credible practitioner who uses the product in real life (independent contractor, experienced DIYer, home cook); best for expanding into adjacent audiences who aspire to the use case."
                                    >
                                        <FieldSelect
                                            placeholder="Select Expert Type"
                                            objectOptions={briefExpertTypeOptions}
                                            name="expert_type"
                                        />
                                    </PanelWrapper>
                                    <PanelWrapper body="Age Range*">
                                        <AgeRangeSelector />
                                    </PanelWrapper>
                                    {areProfessionsLoaded && (
                                        <PanelWrapper body="Profession/Expertise*">
                                            <BriefProfessions
                                                ref={professionsFormRef}
                                                professions={values.professions || []}
                                                updateProfessions={(professions) => {
                                                    const cleanedProfessions = cleanProfessionsData(professions);
                                                    setFieldValue("professions", cleanedProfessions, true);
                                                    setFieldTouched("professions", true, false);
                                                }}
                                                onProfessionUpdate={(professions) => {
                                                    const cleanedProfessions = cleanProfessionsData(professions);
                                                    setFieldValue("professions", cleanedProfessions, true);
                                                }}
                                            />
                                        </PanelWrapper>
                                    )}
                                    <InputPanel name="expert_interests" body="Interests*" />
                                    <InputPanel
                                        name="other"
                                        body="Other Requirements for Product Use: *"
                                        footer="For Example: Requires Google account, Requires specific phone type, etc."
                                    />
                                    <InputPanel
                                        name="special_requests"
                                        body="List any filming appearance requests for selected Expert*"
                                        footer="For Example: clothing type, display of piercings/tattoos, nail style, hair style, etc."
                                    />
                                    <BriefAttachments
                                        brandId={resolveProjectBrandId(project)}
                                        projectId={project?.id}
                                        publicToken={publicToken}
                                        publicBase={publicBase}
                                        ref={attachmentsRef}
                                        value={attachments}
                                        onChange={setAttachments}
                                        onQueueChange={setAttachmentsQueued}
                                        disabled={loading}
                                    />
                                </Box>
                            </Form>
                        </DialogContent>

                        <DialogActions className="!px-6 !py-4 border-t border-border-token">
                            <Box className="w-full flex max-sm:flex-col max-sm:items-stretch max-sm:gap-3 items-center justify-between">
                                <Button
                                    size="medium"
                                    onClick={onCancel}
                                    variant="soft"
                                    color="default"
                                    className="!min-w-[79px] !h-10 px-4 !font-[900] !leading-[100%] max-sm:!w-full"
                                >
                                    Cancel
                                </Button>
                                <Box className="flex max-sm:flex-col max-sm:w-full gap-3">
                                    <Button
                                        size="large"
                                        onClick={handleSave}
                                        variant="contained"
                                        disabled={!changed || loading}
                                        className="max-sm:!w-full"
                                    >
                                        Save for Later
                                    </Button>
                                    <span
                                        className="max-sm:block max-sm:w-full"
                                        title={
                                            !canSubmit
                                                ? "All required fields must be filled out to submit this brief"
                                                : undefined
                                        }
                                    >
                                        <Button
                                            size="large"
                                            onClick={handleSubmit}
                                            variant="contained"
                                            disabled={!canSubmit || loading}
                                            className="max-sm:!w-full"
                                        >
                                            Submit For Review
                                        </Button>
                                    </span>
                                </Box>
                            </Box>
                        </DialogActions>
                    </>
                );
            }}
        </Formik>
    );
};

export default BriefForm;
